import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, XCircle, FileText, MapPin, User, Phone, Mail, Loader2, ScrollText, ChevronDown, ChevronUp, PenLine } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NOTE (client-facing English only): this page is opened by the CUSTOMER,
// never by an internal CRM user, so its copy must NEVER depend on the
// admin's language toggle (es/en) or browser locale — always English.
// A local hardcoded dictionary (instead of react-i18next's useTranslation)
// guarantees that regardless of any localStorage/browser language setting.
const EN = {
  statusBadges: { draft: 'Draft', sent: 'Sent', viewed: 'Viewed', accepted: 'Accepted', rejected: 'Declined' },
  publicEstimate: {
    title: 'Estimate',
    notFound: 'Estimate not found or invalid link',
    estimateDetails: 'Estimate Details',
    item: 'Item', quantity: 'Qty', price: 'Price', total: 'Total', sku: 'SKU',
    subtotal: 'Subtotal', notes: 'Notes',
    contractTitle: 'Service Contract', contractSubtitle: 'Terms and conditions of service',
    acceptTerms: 'I have read and agree to the terms and conditions',
    acceptTermsRequired: 'You must agree to the terms before you can respond',
    paymentMethods: 'Accepted Payment Methods', noCharge: 'No charge', cardSurcharge: '+3.5%-4%',
    estimateAccepted: 'Estimate Accepted', companyName: 'Blue Well Drilling',
    estimateDeclined: 'Estimate Declined',
    approve: 'Approve', decline: 'Decline', declineReasonLabel: 'Please tell us why', declineReasonPlaceholder: 'Reason for declining this estimate',
    declineReasonRequired: 'Please enter a reason before declining.', confirmDecline: 'Confirm Decline', cancel: 'Cancel',
  },
};
const t = (key) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), EN) ?? key;

function StatusBadge({ status }) {
  const STATUS_CONFIG = {
    'draft': { label: t('statusBadges.draft'), class: 'bg-gray-500/20 text-gray-200' },
    'sent': { label: t('statusBadges.sent'), class: 'bg-blue-500/20 text-blue-200' },
    'viewed': { label: t('statusBadges.viewed'), class: 'bg-sky-500/20 text-sky-200' },
    'accepted': { label: t('statusBadges.accepted'), class: 'bg-emerald-500/20 text-emerald-200' },
    'rejected': { label: t('statusBadges.rejected'), class: 'bg-red-500/20 text-red-200' },
  };
  const config = STATUS_CONFIG[status] || { label: status, class: 'bg-gray-500/20 text-gray-200' };
  return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.class}`}>{config.label}</span>;
}

export default function PublicEstimatePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [contractExpanded, setContractExpanded] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signingError, setSigningError] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [isDeclining, setIsDeclining] = useState(false);
  const signSectionRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/public/estimate/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => {
        setData(d);
        if (d.estimate?.status === 'accepted') setAccepted(true);
        if (d.estimate?.status === 'rejected') setDeclined(true);
      })
      .catch(() => setError(t('publicEstimate.notFound')))
      .finally(() => setLoading(false));
    
    // Fetch contract
    fetch(`${API_URL}/api/settings/contract/public`)
      .then(r => r.json())
      .then(c => setContract(c))
      .catch(() => {});
  }, [token]);

  const hasContract = !!(data?.estimate?.include_contract !== false && contract?.content);

  const handleAccept = async () => {
    setSigningError('');
    if (!signerName.trim()) {
      setSigningError('Please enter your full name.');
      return;
    }
    if (hasContract && !agreedToTerms) {
      setSigningError(t('publicEstimate.acceptTermsRequired'));
      return;
    }
    setIsSigning(true);
    try {
      const res = await fetch(`${API_URL}/api/public/estimate/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signer_name: signerName.trim() })
      });
      if (res.ok) {
        setAccepted(true);
        setData(prev => ({ ...prev, estimate: { ...prev.estimate, status: 'accepted', client_signer_name: signerName.trim() } }));
      } else {
        const body = await res.json().catch(() => ({}));
        setSigningError(body.detail || 'Could not approve the estimate. Please try again.');
      }
    } catch { setSigningError('Connection error. Please try again.'); } finally {
      setIsSigning(false);
    }
  };

  const handleDecline = async () => {
    setSigningError('');
    if (hasContract && !agreedToTerms) {
      setSigningError(t('publicEstimate.acceptTermsRequired'));
      return;
    }
    if (!declineReason.trim()) {
      setSigningError(t('publicEstimate.declineReasonRequired'));
      return;
    }
    setIsDeclining(true);
    try {
      const res = await fetch(`${API_URL}/api/public/estimate/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason.trim(), signer_name: signerName.trim() || null })
      });
      if (res.ok) {
        setDeclined(true);
        setData(prev => ({ ...prev, estimate: { ...prev.estimate, status: 'rejected', rejection_reason: declineReason.trim() } }));
      } else {
        const body = await res.json().catch(() => ({}));
        setSigningError(body.detail || 'Could not decline the estimate. Please try again.');
      }
    } catch { setSigningError('Connection error. Please try again.'); } finally {
      setIsDeclining(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="max-w-md"><CardContent className="p-8 text-center">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500 font-medium">{error}</p>
      </CardContent></Card>
    </div>
  );

  const { estimate, customer, project } = data;

  // Fix H (2026-09-01): Job Site Address showed only the street + county —
  // city/state/zip were already coming back from the API but never
  // rendered. Same fallback-chain idea as the working Bill To block above:
  // try the estimate's own well_* fields first, then estimate.location,
  // then the linked project's location. Never render an empty line/comma.
  const jobCity = estimate.well_city || estimate.location?.city || project?.location?.city || '';
  const jobState = estimate.well_state || estimate.location?.state || project?.location?.state || '';
  const jobZip = estimate.well_zip || estimate.location?.zip_code || project?.location?.zip || '';
  const jobCountyRaw = estimate.property_info?.county || estimate.location?.county || project?.location?.county || '';
  const jobCounty = jobCountyRaw ? (/county/i.test(jobCountyRaw) ? jobCountyRaw : `${jobCountyRaw} County`) : '';

  return (
    <div data-testid="public-estimate-page" className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#001a33] to-[#003366] text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-lg font-bold font-['Barlow']">BW</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold font-['Barlow']">{t('publicEstimate.companyName')}</h1>
              <p className="text-blue-200 text-xs">{t('publicEstimate.title')} {estimate.estimate_number}</p>
            </div>
          </div>
          <StatusBadge status={estimate.status} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Register Owner + Property ID banner */}
        {(estimate.property_info?.owner_name || estimate.property_info?.parcel_id) && (
          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-[#001a33]/5 to-[#003366]/5 border border-[#003366]/10" data-testid="public-estimate-owner-parcel">
            {estimate.property_info?.owner_name && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#003366]/50 font-semibold">Register Owner</p>
                <p className="text-sm font-bold text-[#003366]">{estimate.property_info.owner_name}</p>
              </div>
            )}
            {estimate.property_info?.parcel_id && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-[#003366]/50 font-semibold">Property ID</p>
                <p className="text-sm font-bold text-[#003366] font-mono">{estimate.property_info.parcel_id}</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {customer && (
            <Card className="bg-white"><CardContent className="p-4">
              <p data-testid="mailing-address-label" className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2 flex items-center gap-1"><User className="w-3 h-3" /> Bill To / Mailing Address</p>
              <p className="text-sm font-semibold text-gray-900">{customer.first_name} {customer.last_name}</p>
              {customer.company_name && <p className="text-xs text-gray-500">{customer.company_name}</p>}
              {estimate.mailing_address?.address ? (
                <>
                  <p className="text-xs text-gray-500 mt-1">{estimate.mailing_address.address}</p>
                  {(estimate.mailing_address.city || estimate.mailing_address.state || estimate.mailing_address.zip) && (
                    <p className="text-xs text-gray-500">
                      {[estimate.mailing_address.city, estimate.mailing_address.state].filter(Boolean).join(', ')} {estimate.mailing_address.zip || ''}
                    </p>
                  )}
                </>
              ) : customer.address ? (
                <p className="text-xs text-gray-500 mt-1">{customer.address}</p>
              ) : null}
              {customer.phone && <p className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{customer.phone}</p>}
              {customer.email && <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</p>}
            </CardContent></Card>
          )}
          <Card className="bg-white"><CardContent className="p-4">
            <p data-testid="job-site-address-label" className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Job Site Address</p>
            {estimate.well_address ? (
              <>
                <p className="text-sm font-semibold text-gray-900">{estimate.well_address}</p>
                {(jobCity || jobState || jobZip) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {[jobCity, jobState].filter(Boolean).join(', ')} {jobZip}
                  </p>
                )}
                {jobCounty && <p className="text-xs text-gray-500">{jobCounty}</p>}
              </>
            ) : project ? (
              <>
                <p className="text-sm font-semibold text-gray-900">{project.title}</p>
                <p className="text-xs text-gray-500 font-mono">{project.project_number}</p>
                {project.location?.address && <p className="text-xs text-gray-500 mt-1">{project.location.address}</p>}
                {(jobCity || jobState || jobZip) && (
                  <p className="text-xs text-gray-500">
                    {[jobCity, jobState].filter(Boolean).join(', ')} {jobZip}
                  </p>
                )}
                {jobCounty && <p className="text-xs text-gray-500">{jobCounty}</p>}
              </>
            ) : (
              <p className="text-xs text-gray-400 italic">No job site address specified</p>
            )}
          </CardContent></Card>
        </div>

        <Card className="bg-white">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">{t('publicEstimate.estimateDetails')}</h3>
            </div>
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">{t('publicEstimate.item')}</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">{t('publicEstimate.quantity')}</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">{t('publicEstimate.price')}</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">{t('publicEstimate.total')}</th>
              </tr></thead>
              <tbody>
                {(estimate.items || []).map((item, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="px-4 py-3">
                      {item.sku && <p className="text-[10px] text-blue-600 font-mono">{t('publicEstimate.sku')}: {item.sku}</p>}
                      <p className="text-sm font-medium text-gray-800">{item.name || item.description}</p>
                      {item.name && item.description && item.description !== item.name && (
                        <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right align-top">{item.quantity} {item.unit || 'unit'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right align-top">${(item.unit_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right align-top">${(item.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-4 bg-gray-50 flex justify-end">
              <div className="text-right space-y-1">
                <p className="text-sm text-gray-500">{t('publicEstimate.subtotal')}: <span className="font-medium text-gray-700">${(estimate.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></p>
                {estimate.discount_amount > 0 && (
                  <p className="text-sm text-emerald-600">
                    Discount: <span className="font-medium">-${(estimate.discount_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </p>
                )}
                <p className="text-lg font-bold text-[#003366]">{t('publicEstimate.total')}: ${(estimate.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Respond CTA — jumps to the Approve/Decline section below */}
        {!accepted && !declined && ['sent', 'draft', 'viewed'].includes(estimate.status) && (
          <button
            type="button"
            data-testid="approve-and-sign-cta"
            onClick={() => signSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl shadow-lg text-base font-semibold transition-colors"
          >
            <PenLine className="w-5 h-5" /> Review &amp; Respond
          </button>
        )}

        {estimate.notes && (
          <Card className="bg-white"><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">{t('publicEstimate.notes')}</p>
            <p className="text-sm text-gray-700">{estimate.notes}</p>
          </CardContent></Card>
        )}

        {/* Contract Section — collapsed by default (Alian: "no despliegues
            los términos desde un principio"); the required checkbox lives
            right below it and gates both Approve and Decline. */}
        {hasContract && (
          <Card className="bg-white border-amber-200">
            <CardContent className="p-0">
              <button
                type="button"
                onClick={() => setContractExpanded(!contractExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between border-b border-amber-100 bg-amber-50/50 hover:bg-amber-50 transition-colors"
                data-testid="contract-accordion-toggle"
              >
                <div className="flex items-center gap-2">
                  <ScrollText className="w-5 h-5 text-amber-600" />
                  <div className="text-left">
                    <h3 className="text-sm font-semibold text-gray-800">{contract.title || t('publicEstimate.contractTitle')}</h3>
                    <p className="text-xs text-amber-600">{t('publicEstimate.contractSubtitle')}</p>
                  </div>
                </div>
                {contractExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {contractExpanded && (
                <div className="p-4 max-h-96 overflow-y-auto bg-white" data-testid="contract-content">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{contract.content}</pre>
                </div>
              )}
              {!accepted && !declined && ['sent', 'draft', 'viewed'].includes(estimate.status) && (
                <div className="px-4 py-3 bg-amber-50/30 border-t border-amber-100">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      data-testid="agree-terms-checkbox"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-gray-700 font-medium">
                      {t('publicEstimate.acceptTerms')}
                    </span>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Methods Info */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-3">{t('publicEstimate.paymentMethods')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <div className="flex items-center justify-between px-2 py-1.5 rounded bg-gray-50">
                <span className="text-gray-600">ACH / Wire</span>
                <span className="text-emerald-600 font-medium">{t('publicEstimate.noCharge')}</span>
              </div>
              <div className="flex items-center justify-between px-2 py-1.5 rounded bg-gray-50">
                <span className="text-gray-600">Zelle</span>
                <span className="text-emerald-600 font-medium">{t('publicEstimate.noCharge')}</span>
              </div>
              <div className="flex items-center justify-between px-2 py-1.5 rounded bg-gray-50">
                <span className="text-gray-600">Check</span>
                <span className="text-emerald-600 font-medium">{t('publicEstimate.noCharge')}</span>
              </div>
              <div className="flex items-center justify-between px-2 py-1.5 rounded bg-gray-50">
                <span className="text-gray-600">Cash</span>
                <span className="text-emerald-600 font-medium">{t('publicEstimate.noCharge')}</span>
              </div>
              <div className="flex items-center justify-between px-2 py-1.5 rounded bg-amber-50 col-span-2 sm:col-span-1">
                <span className="text-gray-600">Credit / Debit Card</span>
                <span className="text-amber-600 font-semibold">{t('publicEstimate.cardSurcharge')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approve / Decline Section — two buttons, both gated by the terms
            checkbox above (Alian: "no lo pueda poner si no le da el click a
            los terms... approve or deny"). */}
        {!accepted && !declined && ['sent', 'draft', 'viewed'].includes(estimate.status) && (
          <Card ref={signSectionRef} className="bg-white border-[#003366]/20">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <PenLine className="w-5 h-5 text-[#003366]" />
                <h3 className="text-sm font-semibold text-gray-800">Approve or Decline</h3>
              </div>

              {!showDeclineForm && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-600">Full Name <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="signer-name-input"
                    value={signerName}
                    onChange={e => setSignerName(e.target.value)}
                    placeholder="Enter your full name"
                    className="text-sm"
                  />
                </div>
              )}

              {hasContract && !agreedToTerms && (
                <p className="text-xs text-amber-600 font-medium" data-testid="agree-terms-warning">{t('publicEstimate.acceptTermsRequired')}</p>
              )}
              {signingError && (
                <p className="text-xs text-red-500 font-medium">{signingError}</p>
              )}

              {showDeclineForm ? (
                <div className="space-y-3" data-testid="decline-reason-section">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">{t('publicEstimate.declineReasonLabel')} <span className="text-red-500">*</span></label>
                    <Textarea
                      data-testid="decline-reason-input"
                      value={declineReason}
                      onChange={e => setDeclineReason(e.target.value)}
                      placeholder={t('publicEstimate.declineReasonPlaceholder')}
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setShowDeclineForm(false); setSigningError(''); }}
                      data-testid="cancel-decline-btn"
                    >
                      {t('publicEstimate.cancel')}
                    </Button>
                    <Button
                      type="button"
                      data-testid="confirm-decline-btn"
                      onClick={handleDecline}
                      disabled={isDeclining || (hasContract && !agreedToTerms) || !declineReason.trim()}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      {isDeclining ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><XCircle className="w-4 h-4 mr-2" /> {t('publicEstimate.confirmDecline')}</>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="decline-estimate-btn"
                    onClick={() => setShowDeclineForm(true)}
                    disabled={hasContract && !agreedToTerms}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 py-3 text-base rounded-xl disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5 mr-2" /> {t('publicEstimate.decline')}
                  </Button>
                  <Button
                    type="button"
                    data-testid="accept-estimate-btn"
                    onClick={handleAccept}
                    disabled={isSigning || (hasContract && !agreedToTerms)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-base rounded-xl shadow-lg disabled:opacity-50"
                  >
                    {isSigning ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><CheckCircle2 className="w-5 h-5 mr-2" /> {t('publicEstimate.approve')}</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {accepted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-3" data-testid="estimate-accepted-panel">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">{t('publicEstimate.estimateAccepted')}</p>
                {estimate.client_signer_name && (
                  <p className="text-xs text-emerald-600">Signed by: <strong>{estimate.client_signer_name}</strong></p>
                )}
              </div>
            </div>
          </div>
        )}

        {declined && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 space-y-3" data-testid="estimate-declined-panel">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-800">{t('publicEstimate.estimateDeclined')}</p>
                {estimate.rejection_reason && (
                  <p className="text-xs text-red-600">{estimate.rejection_reason}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
